import Layout from '../../components/layout';

import { BounceDetection, i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import React from 'react';

import Clients from '../../components/clients/clients';
import EmailOnly, {
  EmailOnlyLayouts,
  EmailOnlyThemes
} from '../../components/email-only/email-only';
import FeatureHighlights from '../../components/feature-highlights/feature-highlights';
import Footer from '../../components/footer/footer';
import MoreExamples from '../../components/more-examples/more-examples';
import Navbar from '../../components/navbar/navbar';
import LayoutContext from '../../contexts/layoutContext';
import {
  Drupal_FieldNodeTourPageFieldFeatureSections,
  Drupal_MenuLinkContentMoreExamplesList,
  Drupal_NodeTourPage,
  Drupal_ParagraphFeatureSection,
  Drupal_ParagraphParagraphBundle
} from '../../generated/graphql';
import {
  useMenuDefaultFooter,
  useMenuMainNavLinks
} from '../../hooks/use-menu';
import { isDefined, TemplateProps } from '../../types/types';
import './tour-page.scss';
import { getEntityFields } from '../../utils/getEntityFields';

const Tour = ({ data, pageContext, location }: TemplateProps) => {
  const { drupal, i18nData } = data;
  const { langPrefix, pageType, nid, i18nKey, hideSsr } = pageContext;
  const node = data.drupal.nodeById as Drupal_NodeTourPage;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const moreExamplesData = drupal.menuLinkContentQuery;

  const i18n = i18nFunct(i18nData, i18nKey);
  const buttonText = i18n('mktg-ct-all-sign-up-free');

  const navbarMenu = useMenuMainNavLinks(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  return (
    <Layout
      pageType={pageType}
      location={location}
      langPrefix={langPrefix}
      metatags={entityMetatags}
      nid={nid}
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
    >
      <Navbar menu={navbarMenu} />
      <LayoutContext.Consumer>
        {({ language }) => (
          <main className="tour-page overflow-x--hidden" id="content">
            <header
              className="fifteen-og-header"
              style={{
                backgroundImage: `url(${node.fieldHeroBackground ||
                  'https://d2slcw3kip6qmk.cloudfront.net/app/webroot/persona-pages/img/general-tour-hero-large.jpg'})`
              }}
            >
              <div className="lucid-container">
                <h1 className="header__title">{node.fieldTitle}</h1>
                <p className="header__subtitle">{node.fieldSubTitle}</p>

                <div className="tour-page__cta-wrapper">
                  <EmailOnly
                    pageType={pageType}
                    theme={EmailOnlyThemes.Retro}
                    buttonText={buttonText}
                  />
                </div>
              </div>
            </header>
            {(node.fieldFeatureSections as Drupal_FieldNodeTourPageFieldFeatureSections[]).map(
              ({ entity: sectionEntity }, i) => {
                const section = sectionEntity as Drupal_ParagraphFeatureSection;
                const fieldParagraphBundle =
                  section && section.fieldParagraphBundle
                    ? section.fieldParagraphBundle
                        .filter(isDefined)
                        .map(bundle => bundle.entity)
                        .filter(isDefined)
                        .map(
                          entity => entity as Drupal_ParagraphParagraphBundle
                        )
                    : null;
                const cta = section.fieldCtaLink ? section.fieldCtaLink : null;
                const ctaTitle = cta ? cta.title : '';
                const ctaUrl = cta ? cta.url : { path: '' };
                return (
                  <FeatureHighlights
                    key={section.fieldFeatureHeader || `feature-section-${i}`}
                    featureSection={section}
                    linkText={ctaTitle!}
                    linkPath={ctaUrl!}
                    text={fieldParagraphBundle}
                  />
                );
              }
            )}
            {moreExamplesData && (
              <MoreExamples
                links={moreExamplesData.entities!.map(
                  link => link as Drupal_MenuLinkContentMoreExamplesList
                )}
                showLinks
                noPadding
              />
            )}
            <Clients />
            <BounceDetection i18nKey={language.i18nKey}>
              <h2 className="bounce-detection__header">
                {i18n('mktg-bounce-detection-header')}
              </h2>
              <p
                className="bounce-detection__body"
                dangerouslySetInnerHTML={{
                  __html: i18n('mktg-bounce-detection-p1', {}, ['<em>{}</em>'])
                }}
              />
              <EmailOnly
                bounceDetection
                pageType={pageType}
                theme={EmailOnlyThemes.Retro}
                layout={EmailOnlyLayouts.Stacked}
                buttonText={i18n('mktg-ct-all-sign-up-free')}
                hideSSO
              />
            </BounceDetection>
          </main>
        )}
      </LayoutContext.Consumer>
      <Footer menu={footerMenu} entityTranslations={entityTranslations} />
    </Layout>
  );
};

export default Tour;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeTourPage {
          ...entityFields
          fieldExtraTranslations {
            entity {
              ...extraTranslations
            }
          }
          fieldTitle
          fieldHeroBackground
          fieldSubTitle
          fieldFeatureSections {
            entity {
              ... on Drupal_ParagraphFeatureSection {
                fieldFeatureHeader
                fieldFeatureSubheader
                fieldImageLink
                fieldFeatureSectionBackground
                fieldFeatureSectionLayout
                fieldFeatureSectionLinkPlace
                fieldFeatureImageType
                fieldCtaLink {
                  url {
                    path
                  }
                  title
                }
                fieldParagraphBundle {
                  entity {
                    ... on Drupal_ParagraphParagraphBundle {
                      fieldHeader
                      fieldParagraphText {
                        processed
                      }
                      fieldAlt
                      fieldImageLink
                    }
                  }
                }
              }
            }
          }
        }
      }

      menuLinkContentQuery(
        limit: 1000
        sort: [{ field: "parent", direction: ASC }]
        filter: {
          conditions: [
            {
              field: "bundle"
              operator: EQUAL
              value: ["more-examples-list"]
              language: $LanguageId
            }
          ]
        }
      ) {
        entities(language: $LanguageId) {
          ...menuFragment
        }
      }
    }

    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_email_only_make_a_diagram_variable
            mktg_email_only_make_a_diagram_default
            mktg_email_only_placeholder
            mktg_email_only_registering
            mktg_privacy_form
            mktg_bounce_detection_header
            mktg_bounce_detection_p1
          }
        }
      }
    }
  }
`;
